import React, { useEffect } from "react";

const SECTIONS = [
  {
    h: "Confidentiality",
    p:
      "Everything you see, use, or experience through Protos — including the product, formulation, design, positioning, use cases, and overall system — is confidential. You agree not to share, post, discuss, or distribute any part of this experience publicly or privately without written permission from Protos.",
  },
  {
    h: "Use of Information",
    p:
      "This experience is for personal evaluation and potential collaboration only. You agree not to use any information, insight, or exposure gained through Protos to replicate, reverse engineer, or develop a similar product, system, or concept — either for yourself or for others.",
  },
  {
    h: "Participation Intent",
    p:
      "You are participating as a trusted early user, potential partner, or aligned individual. By joining, you confirm that you are not currently developing a competing product in this category and are not participating for the purpose of competitive research.",
  },
  {
    h: "Ownership",
    p:
      "All concepts, materials, and intellectual property associated with Protos remain the exclusive property of Protos.",
  },
  {
    h: "Term",
    p:
      "These expectations remain in place for two (2) years or until Protos publicly releases the relevant information.",
  },
  {
    h: "Acknowledgment",
    p:
      "This is a private, invitation-level experience. By participating, you agree to respect the integrity of what is being built.",
  },
];

export default function PrivacyAgreement({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <div
      data-testid="privacy-modal"
      className={`fixed inset-0 z-[80] transition-all duration-400 ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-xl"
        onClick={onClose}
      />
      <div
        className={`relative h-full overflow-y-auto px-5 md:px-8 lg:px-16 py-20 transition-transform duration-500 ${
          open ? "translate-y-0" : "translate-y-6"
        }`}
      >
        <div className="container-x mx-auto max-w-3xl">
          <div className="flex items-start justify-between gap-6 mb-8">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/45">
                Legal · Private Access
              </span>
              <h2
                className="kinetic mt-4"
                style={{
                  fontSize: "clamp(2rem, 5vw, 3.6rem)",
                  lineHeight: 0.95,
                }}
              >
                <span className="gradient-text">Protos Private</span>
                <br />
                <span className="outline-text-blue">Access Agreement.</span>
              </h2>
            </div>
            <button
              data-testid="privacy-close"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 w-11 h-11 inline-flex items-center justify-center rounded-full border border-white/15 hover:border-white/50 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <p className="text-white/65 text-base lg:text-lg leading-relaxed mb-10 max-w-2xl">
            Protos is a private, early-stage performance system being shared with a
            small group of selected participants. By joining this experience, you
            agree to the following:
          </p>

          <div className="space-y-8">
            {SECTIONS.map((s, i) => (
              <section key={i} data-testid={`privacy-section-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="font-mono text-xs"
                    style={{ color: "var(--blue-bright)" }}
                  >
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl lg:text-2xl uppercase text-white">
                    {s.h}
                  </h3>
                </div>
                <p className="text-white/70 text-sm lg:text-base leading-relaxed pl-9">
                  {s.p}
                </p>
              </section>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-4">
            <button
              data-testid="privacy-acknowledge"
              onClick={onClose}
              className="btn-primary"
            >
              <span>I understand</span>
            </button>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">
              © {new Date().getFullYear()} Protos · Private &amp; Confidential
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
